import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Package, ArrowLeft, ArrowRight, Plus, X, Check, FileText, DollarSign, Truck, Award } from 'lucide-react';
import { productsApi, categoriesApi, countriesApi } from '../../services/api';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, label: 'Product Info', icon: Package },
  { id: 2, label: 'Pricing & MOQ', icon: DollarSign },
  { id: 3, label: 'Trade & Shipping', icon: Truck },
  { id: 4, label: 'Review', icon: Check },
];

const AddProductPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEditing = !!productId;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    industry: '',
    subcategory: '',
    sub_subcategory: '',
    product_group: '',
    description: '',
    specifications: '',
    keywords: [],
    images: [],
    video_url: '',
    price_min: '',
    price_max: '',
    price_unit: 'USD',
    moq: '',
    moq_unit: 'pieces',
    packaging: '',
    port: '',
    lead_time: '',
    origin_country: '',
    samples_available: false,
    sample_price: '',
    certifications: [],
    incoterms: '',
    payment_terms: '',
    supply_ability: ''
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [imageInput, setImageInput] = useState('');
  const [certInput, setCertInput] = useState('');

  // Derived category data
  const selectedCategory = categories.find(c => c.name === formData.industry);
  const subcategories = selectedCategory?.subcategories || [];
  const selectedSub = subcategories.find(s => s.name === formData.subcategory);
  const subSubcategories = selectedSub?.subs || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, countriesRes] = await Promise.all([
          categoriesApi.getAll(),
          countriesApi.getAll()
        ]);
        setCategories(categoriesRes.data || []);
        setCountries(countriesRes.data || []);

        if (productId) {
          const { data } = await productsApi.getById(productId);
          setFormData({
            title: data.title || '',
            industry: data.industry || '',
            subcategory: data.subcategory || '',
            sub_subcategory: data.sub_subcategory || '',
            product_group: data.product_group || '',
            description: data.description || '',
            specifications: data.specifications || '',
            keywords: data.keywords || [],
            images: data.images || [],
            video_url: data.video_url || '',
            price_min: data.price_min?.toString() || '',
            price_max: data.price_max?.toString() || '',
            price_unit: data.price_unit || 'USD',
            moq: data.moq?.toString() || '',
            moq_unit: data.moq_unit || 'pieces',
            packaging: data.packaging || '',
            port: data.port || '',
            lead_time: data.lead_time || '',
            origin_country: data.origin_country || '',
            samples_available: data.samples_available || false,
            sample_price: data.sample_price?.toString() || '',
            certifications: data.certifications || [],
            incoterms: data.incoterms || '',
            payment_terms: data.payment_terms || '',
            supply_ability: data.supply_ability || ''
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [productId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addTag = (field, input, setInput) => {
    const val = input.trim();
    if (val && !formData[field].includes(val.toLowerCase ? val.toLowerCase() : val)) {
      setFormData({ ...formData, [field]: [...formData[field], field === 'keywords' ? val.toLowerCase() : val] });
      setInput('');
    }
  };

  const removeTag = (field, val) => {
    setFormData({ ...formData, [field]: formData[field].filter(v => v !== val) });
  };

  const addImage = () => {
    if (imageInput.trim() && !formData.images.includes(imageInput.trim())) {
      setFormData({ ...formData, images: [...formData.images, imageInput.trim()] });
      setImageInput('');
    }
  };

  const removeImage = (img) => {
    setFormData({ ...formData, images: formData.images.filter(i => i !== img) });
  };

  const canProceed = () => {
    if (step === 1) return formData.title && formData.industry && formData.description;
    if (step === 2) return formData.price_min && formData.moq;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        price_min: formData.price_min ? parseFloat(formData.price_min) : null,
        price_max: formData.price_max ? parseFloat(formData.price_max) : null,
        moq: formData.moq ? parseInt(formData.moq) : 1,
        sample_price: formData.sample_price ? parseFloat(formData.sample_price) : null
      };

      if (isEditing) {
        await productsApi.update(productId, submitData);
        toast.success('Product updated successfully!');
      } else {
        await productsApi.create(submitData);
        toast.success('Product created successfully!');
      }
      navigate('/dashboard/supplier');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="container-main py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="product-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEditing ? 'Edit Product' : 'Add New Product'}
              </h1>
              <p className="text-slate-600 text-sm">Step {step} of 4</p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-8 bg-white border border-slate-200 p-3">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => s.id < step && setStep(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors ${
                    step === s.id 
                      ? 'bg-[#0A192F] text-white' 
                      : step > s.id 
                        ? 'bg-green-50 text-green-700 cursor-pointer' 
                        : 'bg-slate-50 text-slate-400'
                  }`}
                  data-testid={`step-${s.id}`}
                >
                  {step > s.id ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > s.id ? 'bg-green-300' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* STEP 1: Product Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" /> Product Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Product Title *</Label>
                    <Input id="title" name="title" value={formData.title} onChange={handleChange}
                      placeholder="e.g., High Power Rectifier Diode 1000V 200A" required className="mt-1" data-testid="product-title-input" />
                  </div>

                  {/* 3-Level Category System */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Main Category *</Label>
                      <Select value={formData.industry} onValueChange={(val) => setFormData({ ...formData, industry: val, subcategory: '', sub_subcategory: '' })}>
                        <SelectTrigger className="mt-1" data-testid="product-industry-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Subcategory</Label>
                      <Select value={formData.subcategory} onValueChange={(val) => setFormData({ ...formData, subcategory: val, sub_subcategory: '' })} disabled={!formData.industry}>
                        <SelectTrigger className="mt-1" data-testid="product-subcategory-select">
                          <SelectValue placeholder={formData.industry ? 'Select subcategory' : 'Select main first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {subcategories.map((sub) => (
                            <SelectItem key={sub.id} value={sub.name}>{sub.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Sub-subcategory</Label>
                      <Select value={formData.sub_subcategory} onValueChange={(val) => setFormData({ ...formData, sub_subcategory: val })} disabled={!formData.subcategory}>
                        <SelectTrigger className="mt-1" data-testid="product-sub-subcategory-select">
                          <SelectValue placeholder={formData.subcategory ? 'Select type' : 'Select sub first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {subSubcategories.map((s, i) => (
                            <SelectItem key={i} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="product_group">Product Group</Label>
                    <Input id="product_group" name="product_group" value={formData.product_group} onChange={handleChange}
                      placeholder="e.g., Power Diodes" className="mt-1" />
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea id="description" name="description" value={formData.description} onChange={handleChange}
                      placeholder="Describe your product in detail..." rows={4} required className="mt-1" data-testid="product-description-input" />
                  </div>

                  <div>
                    <Label htmlFor="specifications">Technical Specifications</Label>
                    <Textarea id="specifications" name="specifications" value={formData.specifications} onChange={handleChange}
                      placeholder="Voltage, current, dimensions, materials, etc." rows={3} className="mt-1" />
                  </div>

                  {/* Keywords */}
                  <div>
                    <Label>Keywords</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)}
                        placeholder="Add keyword" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('keywords', keywordInput, setKeywordInput))} />
                      <Button type="button" variant="outline" onClick={() => addTag('keywords', keywordInput, setKeywordInput)}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {formData.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.keywords.map((kw, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                            {kw}
                            <button type="button" onClick={() => removeTag('keywords', kw)}><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Images */}
                  <div>
                    <Label>Product Images (URLs)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={imageInput} onChange={(e) => setImageInput(e.target.value)} placeholder="Paste image URL" />
                      <Button type="button" variant="outline" onClick={addImage}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {formData.images.map((img, i) => (
                          <div key={i} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removeImage(img)}
                              className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="video_url">Video URL</Label>
                    <Input id="video_url" name="video_url" value={formData.video_url} onChange={handleChange}
                      placeholder="YouTube or video link" className="mt-1" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Pricing & MOQ */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" /> Pricing & Quantity
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="price_min">Min Price *</Label>
                      <Input id="price_min" name="price_min" type="number" step="0.01" value={formData.price_min} onChange={handleChange}
                        placeholder="15.00" required className="mt-1" data-testid="product-price-min" />
                    </div>
                    <div>
                      <Label htmlFor="price_max">Max Price</Label>
                      <Input id="price_max" name="price_max" type="number" step="0.01" value={formData.price_max} onChange={handleChange}
                        placeholder="25.00" className="mt-1" data-testid="product-price-max" />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select value={formData.price_unit} onValueChange={(val) => setFormData({ ...formData, price_unit: val })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['USD', 'EUR', 'GBP', 'AED', 'CNY', 'INR'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="moq">Minimum Order Quantity *</Label>
                      <Input id="moq" name="moq" type="number" value={formData.moq} onChange={handleChange}
                        placeholder="100" required className="mt-1" data-testid="product-moq" />
                    </div>
                    <div>
                      <Label>MOQ Unit</Label>
                      <Select value={formData.moq_unit} onValueChange={(val) => setFormData({ ...formData, moq_unit: val })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['pieces', 'sets', 'kg', 'tons', 'meters', 'liters', 'boxes', 'pallets'].map(u => <SelectItem key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="supply_ability">Supply Ability</Label>
                    <Input id="supply_ability" name="supply_ability" value={formData.supply_ability} onChange={handleChange}
                      placeholder="e.g., 10,000 pieces per month" className="mt-1" data-testid="product-supply-ability" />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Samples Available</p>
                      <p className="text-sm text-slate-500">Allow buyers to request samples</p>
                    </div>
                    <Switch checked={formData.samples_available}
                      onCheckedChange={(checked) => setFormData({ ...formData, samples_available: checked })} />
                  </div>

                  {formData.samples_available && (
                    <div>
                      <Label htmlFor="sample_price">Sample Price</Label>
                      <Input id="sample_price" name="sample_price" type="number" step="0.01" value={formData.sample_price} onChange={handleChange}
                        placeholder="30.00" className="mt-1" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Trade & Shipping */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" /> Trade & Shipping Details
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="origin_country">Origin Country</Label>
                      <Select value={formData.origin_country} onValueChange={(val) => setFormData({ ...formData, origin_country: val })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Select country" /></SelectTrigger>
                        <SelectContent>
                          {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="port">Port of Loading</Label>
                      <Input id="port" name="port" value={formData.port} onChange={handleChange}
                        placeholder="e.g., Jebel Ali, Dubai" className="mt-1" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lead_time">Lead Time</Label>
                      <Input id="lead_time" name="lead_time" value={formData.lead_time} onChange={handleChange}
                        placeholder="e.g., 7-14 days" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="packaging">Packaging Details</Label>
                      <Input id="packaging" name="packaging" value={formData.packaging} onChange={handleChange}
                        placeholder="e.g., 50pcs/box" className="mt-1" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Incoterms</Label>
                      <Select value={formData.incoterms} onValueChange={(val) => setFormData({ ...formData, incoterms: val })}>
                        <SelectTrigger className="mt-1" data-testid="product-incoterms"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['FOB', 'CIF', 'EXW', 'DDP', 'CFR', 'FCA', 'DAP'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Payment Terms</Label>
                      <Select value={formData.payment_terms} onValueChange={(val) => setFormData({ ...formData, payment_terms: val })}>
                        <SelectTrigger className="mt-1" data-testid="product-payment-terms"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {['T/T', 'L/C', 'D/P', 'D/A', 'Western Union', 'PayPal', 'Escrow'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div>
                    <Label>Certifications</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={certInput} onChange={(e) => setCertInput(e.target.value)}
                        placeholder="e.g., ISO 9001, CE, RoHS" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('certifications', certInput, setCertInput))} />
                      <Button type="button" variant="outline" onClick={() => addTag('certifications', certInput, setCertInput)}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {formData.certifications.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.certifications.map((cert, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">
                            <Award className="w-3 h-3" /> {cert}
                            <button type="button" onClick={() => removeTag('certifications', cert)}><X className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Review Your Product
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <div><span className="text-slate-500">Title:</span> <span className="font-medium text-slate-900 ml-1">{formData.title}</span></div>
                    <div><span className="text-slate-500">Category:</span> <span className="font-medium text-slate-900 ml-1">{formData.industry}</span></div>
                    {formData.subcategory && <div><span className="text-slate-500">Subcategory:</span> <span className="font-medium text-slate-900 ml-1">{formData.subcategory}</span></div>}
                    {formData.sub_subcategory && <div><span className="text-slate-500">Type:</span> <span className="font-medium text-slate-900 ml-1">{formData.sub_subcategory}</span></div>}
                    <div><span className="text-slate-500">Price:</span> <span className="font-medium text-slate-900 ml-1">{formData.price_unit} {formData.price_min}{formData.price_max ? ` - ${formData.price_max}` : ''}</span></div>
                    <div><span className="text-slate-500">MOQ:</span> <span className="font-medium text-slate-900 ml-1">{formData.moq} {formData.moq_unit}</span></div>
                    {formData.origin_country && <div><span className="text-slate-500">Origin:</span> <span className="font-medium text-slate-900 ml-1">{formData.origin_country}</span></div>}
                    {formData.lead_time && <div><span className="text-slate-500">Lead Time:</span> <span className="font-medium text-slate-900 ml-1">{formData.lead_time}</span></div>}
                    {formData.incoterms && <div><span className="text-slate-500">Incoterms:</span> <span className="font-medium text-slate-900 ml-1">{formData.incoterms}</span></div>}
                    {formData.payment_terms && <div><span className="text-slate-500">Payment:</span> <span className="font-medium text-slate-900 ml-1">{formData.payment_terms}</span></div>}
                    {formData.supply_ability && <div><span className="text-slate-500">Supply Ability:</span> <span className="font-medium text-slate-900 ml-1">{formData.supply_ability}</span></div>}
                    <div><span className="text-slate-500">Samples:</span> <span className="font-medium text-slate-900 ml-1">{formData.samples_available ? 'Yes' : 'No'}</span></div>
                  </div>
                  {formData.description && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-1">Description</p>
                      <p className="text-sm text-slate-700">{formData.description}</p>
                    </div>
                  )}
                  {formData.keywords.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-1">Keywords</p>
                      <div className="flex flex-wrap gap-1">
                        {formData.keywords.map((kw, i) => <span key={i} className="px-2 py-0.5 bg-slate-100 text-xs rounded">{kw}</span>)}
                      </div>
                    </div>
                  )}
                  {formData.certifications.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-1">Certifications</p>
                      <div className="flex flex-wrap gap-1">
                        {formData.certifications.map((cert, i) => <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{cert}</span>)}
                      </div>
                    </div>
                  )}
                  {formData.images.length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-1">Images ({formData.images.length})</p>
                      <div className="grid grid-cols-6 gap-2">
                        {formData.images.map((img, i) => (
                          <div key={i} className="aspect-square bg-slate-100 rounded overflow-hidden">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-6">
            {step > 1 && (
              <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(step - 1)} data-testid="product-prev-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Previous
              </Button>
            )}
            {step < 4 ? (
              <Button type="button" className={`flex-1 bg-[#0A192F] hover:bg-[#172A46] ${step === 1 ? '' : ''}`}
                disabled={!canProceed()} onClick={() => setStep(step + 1)} data-testid="product-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button type="button" className="flex-1 bg-[#EA580C] hover:bg-[#C2410C]"
                disabled={loading} onClick={handleSubmit} data-testid="product-submit-btn">
                {loading ? 'Saving...' : (isEditing ? 'Update Product' : 'Publish Product')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductPage;
